import React, { useState, useEffect, useCallback } from 'react';
import { servicosApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Loader2, Wrench, Tags } from 'lucide-react';

const emptyServico = {
  nome: '',
  descricao: '',
  preco_base: 0,
  categoria_id: '',
  unidade: 'servico'
};

const Servicos = () => {
  const [servicos, setServicos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedServico, setSelectedServico] = useState(null);
  const [formData, setFormData] = useState({ ...emptyServico });
  const [saving, setSaving] = useState(false);

  // Categoria management
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ nome: '' });
  const [savingCat, setSavingCat] = useState(false);
  const [catToDelete, setCatToDelete] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [servRes, catRes] = await Promise.all([servicosApi.list(), servicosApi.listCategorias()]);
      setServicos(servRes.data);
      setCategorias(catRes.data);
    } catch (error) {
      console.error('Error loading:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleOpenForm = (servico = null) => {
    if (servico) {
      setSelectedServico(servico);
      setFormData({
        nome: servico.nome,
        descricao: servico.descricao || '',
        preco_base: servico.preco_base || 0,
        categoria_id: servico.categoria_id || '',
        unidade: servico.unidade || 'servico'
      });
    } else {
      setSelectedServico(null);
      setFormData({ ...emptyServico });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedServico(null);
    setFormData({ ...emptyServico });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      toast.error('Informe o nome do serviço');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.categoria_id) payload.categoria_id = null;
      if (selectedServico) {
        await servicosApi.update(selectedServico.id, payload);
        toast.success('Serviço atualizado!');
      } else {
        await servicosApi.create(payload);
        toast.success('Serviço criado!');
      }
      handleCloseForm();
      loadData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar serviço');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedServico) return;
    try {
      await servicosApi.delete(selectedServico.id);
      toast.success('Serviço excluído!');
      setShowDeleteDialog(false);
      setSelectedServico(null);
      loadData();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao excluir serviço');
    }
  };

  // ============ CATEGORIA HANDLERS ============
  const handleSaveCategoria = async (e) => {
    e.preventDefault();
    if (!catForm.nome.trim()) {
      toast.error('Informe o nome da categoria');
      return;
    }
    setSavingCat(true);
    try {
      if (editingCat) {
        await servicosApi.updateCategoria(editingCat.id, { nome: catForm.nome });
        toast.success('Categoria atualizada!');
      } else {
        await servicosApi.createCategoria({ nome: catForm.nome });
        toast.success('Categoria criada!');
      }
      setEditingCat(null);
      setCatForm({ nome: '' });
      loadData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar categoria');
    } finally {
      setSavingCat(false);
    }
  };

  const handleDeleteCategoria = async () => {
    if (!catToDelete) return;
    try {
      await servicosApi.deleteCategoria(catToDelete.id);
      toast.success('Categoria excluída');
      setCatToDelete(null);
      loadData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir categoria');
      setCatToDelete(null);
    }
  };

  const filteredServicos = servicos.filter((s) => {
    const matchesSearch =
      s.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.descricao || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCat =
      categoriaFilter === 'all'
        ? true
        : categoriaFilter === '__none__'
          ? !s.categoria_id
          : s.categoria_id === categoriaFilter;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="space-y-6" data-testid="servicos-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            Serviços
          </h1>
          <p className="text-gray-500 mt-1">
            Gerencie seu catálogo de serviços e categorias
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowCatDialog(true)} data-testid="manage-categorias-servico-btn">
            <Tags size={18} className="mr-2" />
            Gerenciar Categorias
          </Button>
          <Button onClick={() => handleOpenForm()} data-testid="add-servico-btn">
            <Plus size={18} className="mr-2" />
            Novo Serviço
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <Wrench size={20} />
              Lista de Serviços ({filteredServicos.length})
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-categoria-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  <SelectItem value="__none__">Sem categoria</SelectItem>
                  {categorias.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Buscar serviço..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-servico-input"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
          ) : filteredServicos.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm || categoriaFilter !== 'all' ? 'Nenhum serviço encontrado' : 'Nenhum serviço cadastrado'}
            </div>
          ) : (
            <div className="table-fit">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Categoria</th>
                    <th>Descrição</th>
                    <th>Valor</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredServicos.map((servico) => (
                    <tr key={servico.id} data-testid={`servico-row-${servico.id}`}>
                      <td className="font-medium">{servico.nome}</td>
                      <td>
                        {servico.categoria_nome ? (
                          <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">
                            {servico.categoria_nome}
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="max-w-xs truncate text-gray-500">
                        {servico.descricao || '—'}
                      </td>
                      <td className="font-semibold text-[#2B7A3D] tabular-nums">
                        {formatCurrency(servico.preco_base)}{servico.unidade === 'm2' && <span className="text-xs text-gray-500">/m²</span>}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenForm(servico)} data-testid={`edit-servico-${servico.id}`}>
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedServico(servico); setShowDeleteDialog(true); }}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-servico-${servico.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Serviço Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              {selectedServico ? 'Editar Serviço' : 'Novo Serviço'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do serviço
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
                placeholder="Ex: Instalação de chuveiro"
                data-testid="servico-nome-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="categoria">Categoria</Label>
              <Select
                value={formData.categoria_id || undefined}
                onValueChange={(v) => setFormData({ ...formData, categoria_id: v === '__none__' ? '' : v })}
              >
                <SelectTrigger data-testid="servico-categoria-select">
                  <SelectValue placeholder="Selecione uma categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem categoria</SelectItem>
                  {categorias.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      Nenhuma categoria. Use &quot;Gerenciar Categorias&quot;.
                    </div>
                  ) : (
                    categorias.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="preco_base">{formData.unidade === 'm2' ? 'Valor por m² (R$) *' : 'Valor (R$) *'}</Label>
              <Input
                id="preco_base"
                type="number"
                min="0"
                step="0.01"
                value={formData.preco_base}
                onChange={(e) => setFormData({ ...formData, preco_base: parseFloat(e.target.value) || 0 })}
                data-testid="servico-preco-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo de cobrança</Label>
              <Select value={formData.unidade} onValueChange={(v) => setFormData({ ...formData, unidade: v })}>
                <SelectTrigger data-testid="servico-unidade-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="servico">Por serviço (quantidade inteira)</SelectItem>
                  <SelectItem value="m2">Por m² (preço × metragem)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descreva o serviço (opcional)..."
                rows={3}
                data-testid="servico-descricao-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#2B7A3D] hover:bg-[#1f5a2c] text-white" data-testid="save-servico-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando...</> : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Serviço */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o serviço &quot;{selectedServico?.nome}&quot;? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Categorias Dialog */}
      <Dialog open={showCatDialog} onOpenChange={(o) => { setShowCatDialog(o); if (!o) { setEditingCat(null); setCatForm({ nome: '' }); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <Tags size={20} /> Gerenciar Categorias de Serviço
            </DialogTitle>
            <DialogDescription>
              Crie, edite e exclua categorias. Categorias em uso não podem ser removidas.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveCategoria} className="space-y-3 p-4 bg-gray-50 rounded-lg border">
            <p className="font-medium text-sm" style={{ fontFamily: 'Work Sans' }}>
              {editingCat ? 'Editar categoria' : 'Nova categoria'}
            </p>
            <div className="flex gap-2">
              <Input
                value={catForm.nome}
                onChange={(e) => setCatForm({ nome: e.target.value })}
                placeholder="Ex: Elétrica"
                required
                data-testid="cat-servico-nome-input"
              />
              {editingCat && (
                <Button type="button" variant="outline" onClick={() => { setEditingCat(null); setCatForm({ nome: '' }); }}>
                  Cancelar
                </Button>
              )}
              <Button type="submit" disabled={savingCat} className="bg-[#2B7A3D] hover:bg-[#1f5a2c] text-white" data-testid="save-cat-servico-btn">
                {savingCat ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingCat ? 'Atualizar' : 'Adicionar')}
              </Button>
            </div>
          </form>

          <div className="mt-4 space-y-2">
            {categorias.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Nenhuma categoria cadastrada</p>
            ) : (
              categorias.map((c) => (
                <div key={c.id} className="flex items-center justify-between p-2 border rounded-md bg-white" data-testid={`cat-servico-row-${c.id}`}>
                  <span className="text-sm font-medium">{c.nome}</span>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingCat(c); setCatForm({ nome: c.nome }); }} data-testid={`edit-cat-servico-${c.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setCatToDelete(c)} data-testid={`delete-cat-servico-${c.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCatDialog(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Categoria confirm */}
      <AlertDialog open={!!catToDelete} onOpenChange={(o) => !o && setCatToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja excluir a categoria &quot;{catToDelete?.nome}&quot;? Se estiver em uso por algum serviço, a exclusão será bloqueada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategoria} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-cat-servico-btn">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Servicos;
